import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useToast } from '../context/ToastContext';
import { getProductImageUrl } from '../utils/productImage';

const C = { primary: '#8B1A1A', gold: '#D4A017', bg: '#FFF8F0' };

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart: addToCartFn } = useCart();
  const { isWishlisted, toggleWishlist: toggleWishlistFn } = useWishlist();
  const { addToast } = useToast();

  const wishlisted = isWishlisted(product.id);

  const imageUrl = getProductImageUrl(
    product,
    `https://placehold.co/400x500/8B1A1A/gold?text=${encodeURIComponent(product.name.slice(0, 20))}`
  );

  const addToCart = (e) => {
    e.stopPropagation();
    addToCartFn(product, 1);
    addToast(`${product.name} added to cart`, 'success');
  };

  const toggleWishlist = (e) => {
    e.stopPropagation();
    toggleWishlistFn(product);
    addToast(
      wishlisted ? `${product.name} removed from wishlist` : `${product.name} saved to wishlist`,
      wishlisted ? 'info' : 'success'
    );
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        position: 'relative',
      }}
      onClick={() => navigate(`/product/${product.slug}`)}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(139,26,26,0.15)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)';
      }}
    >
      <div style={{ position: 'relative' }}>
        <img
          src={imageUrl}
          alt={product.name}
          style={{ width: '100%', height: 260, objectFit: 'cover', display: 'block' }}
          onError={e => { e.target.src = `https://placehold.co/400x500/8B1A1A/gold?text=A1+Looks`; }}
        />
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {product.is_new_arrival && (
            <span style={{ background: C.gold, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 3 }}>NEW</span>
          )}
          {product.is_best_seller && (
            <span style={{ background: C.primary, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 3 }}>BESTSELLER</span>
          )}
          {!product.in_stock && (
            <span style={{ background: '#666', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 3 }}>OUT OF STOCK</span>
          )}
        </div>
        {product.sale_price && (
          <div style={{ position: 'absolute', top: 10, right: 44, background: '#e53935', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 3 }}>SALE</div>
        )}
        <button
          onClick={toggleWishlist}
          title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: wishlisted ? C.primary : 'rgba(255,255,255,0.9)',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: wishlisted ? '#fff' : C.primary,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            transition: 'background 0.2s, color 0.2s',
          }}
        >
          {wishlisted ? '♥' : '♡'}
        </button>
      </div>

      <div style={{ padding: '14px 14px 16px' }}>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>{product.category_name}</p>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: '#2C2C2C', marginBottom: 8, lineHeight: 1.4, height: 42, overflow: 'hidden' }}>
          {product.name}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>
            ₹{Math.round(parseFloat(product.effective_price || product.price)).toLocaleString('en-IN')}
          </span>
          {product.sale_price && (
            <span style={{ fontSize: 13, color: '#999', textDecoration: 'line-through' }}>
              ₹{Math.round(parseFloat(product.price)).toLocaleString('en-IN')}
            </span>
          )}
        </div>
        <button
          onClick={addToCart}
          disabled={!product.in_stock}
          style={{
            width: '100%',
            padding: '9px 0',
            background: product.in_stock ? C.primary : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            cursor: product.in_stock ? 'pointer' : 'not-allowed',
            letterSpacing: 0.5,
          }}
        >
          {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
        </button>
      </div>
    </div>
  );
}
